p<template>
    <div>
        <div class="row">
            <div class="col-12 mb-4">
                <multiselect
                    :allowEmpty="false"
                    :options="typeOptions"
                    v-model="selectedType"
                    :searchable="false"
                    :show-labels="false"
                >
                    <span slot="caret" slot-scope="{ toggle }" class="arrow">
                        <img
                            src="/images/caret-down.svg"
                            alt="dropdown select"
                        />
                    </span>
                </multiselect>
            </div>

            <div class="col-6">
                <multiselect
                    :allowEmpty="false"
                    :options="optionsLeft"
                    v-model="selectedLeft"
                    :searchable="false"
                    :show-labels="false"
                >
                    <span slot="caret" slot-scope="{ toggle }" class="arrow">
                        <img
                            src="/images/caret-down.svg"
                            alt="dropdown select"
                        />
                    </span>
                </multiselect>
            </div>
            <div class="col-6">
                <multiselect
                    :allowEmpty="false"
                    :options="optionsRight"
                    v-model="selectedRight"
                    :searchable="false"
                    label="label"
                    :show-labels="false"
                >
                    <span slot="caret" slot-scope="{ toggle }" class="arrow">
                        <img
                            src="/images/caret-down.svg"
                            alt="dropdown select"
                        />
                    </span>
                </multiselect>
            </div>
        </div>

        <div id="chart" class="chart" v-if="ready">
            <div v-if="isProductivitySummary">
                <div v-if="series.length > 0">
                    <div class="chart-title">
                        Office Productivity Chart
                    </div>
                    <div class="chart-desc">
                        {{percentageText}}
                    </div>
                    <apexchart
                        ref="donutChart"
                        type="donut"
                        :options="chartOptions"
                        :series="series"
                        class="pie-chart"
                        height="400"
                    ></apexchart>
                </div>
                <div v-else class="text-center w-100">
                    No data for selected options
                </div>
            </div>
          <div v-if="isProductivityHourly">
            <div v-if="series.length > 0">
              <div class="chart-title">
                Office Productivity Chart (Hourly)
              </div>
              <div class="chart-desc">
                {{percentageText}}
              </div>
              <apexchart
                  ref="donutChart"
                  type="donut"
                  :options="chartOptions"
                  :series="series"
                  class="pie-chart"
                  height="400"
              ></apexchart>
            </div>
            <div v-else class="text-center w-100">
              No data for selected options
            </div>
          </div>
            <div v-if="isGoal">
                <GoalMetrics
                    :company="company"
                    :userGroups="userGroups"
                    :currentUser="currentUser"
                    :selectedLeft="selectedLeft"
                    :selectedRight="selectedRight"
                />
            </div>
        </div>
    </div>
</template>

<script>
    import VueApexCharts from 'vue-apexcharts'
    import Multiselect from 'vue-multiselect'
    import _ from 'lodash'
    import GoalMetrics from './GoalMetrics';
    import moment from 'moment';

    const DEFAULT_TYPE = 'Productivity (Summary)';
    const DEFAULT_RIGHT_SELECTION = {name: 'personal', label: 'Myself'};
    const DEFAULT_LEFT_SELECTION = 'Today';

    export default {
        components: {
            GoalMetrics,
            apexchart: VueApexCharts,
            Multiselect,
        },

        props: ['company', 'userGroups', 'currentUser'],

        data () {
            return {
                series: [],
                totalTasks: 0,
                totalCompletedTasks: 0,
                ready: false,
                chartOptions: {
                    chart: {
                        type: 'donut',
                    },
                    plotOptions: {
                        pie: {
                            donut: {
                                size: '50%',
                            },
                        },
                    },
                    legend: {
                        position: 'bottom',
                    },
                    labels: [],
                    tooltip: {
                        enabled: true,
                        y: {
                            formatter: undefined,
                            title: {
                                formatter: seriesName => seriesName,
                            },
                        },

                        custom: function ({
                            series,
                            seriesIndex,
                            dataPointIndex,
                            w,
                        }) {
                            let total = 0
                            for (let x of series) {
                                total += x
                            }
                            let selected = series[seriesIndex]

                            return (
                                '<div class=\'tooltip-chart\'>' +
                                w.config.labels[seriesIndex] +
                                ': ' +
                                '<span>' +
                                Math.floor((selected / total) * 100) +
                                '%</span>' +
                                '</div>'
                            )
                        },
                    },
                },
                optionsRight: [{name: 'personal', label: 'Myself'}],
                selectedRight: localStorage.activityChart_selectedRight ? JSON.parse(localStorage.activityChart_selectedRight) : DEFAULT_RIGHT_SELECTION,
                optionsLeft: ['Today', 'Week', 'Month'],
                selectedLeft: localStorage.activityChart_selectedLeft ?? DEFAULT_LEFT_SELECTION,
                typeOptions: ['Productivity (Summary)', 'Productivity (Hourly)', 'Goal Metrics'],
                selectedType: localStorage.activityChart_selectedType ?? DEFAULT_TYPE,
            }
        },

        computed: {
            percentageText () {
                const percentage = (this.totalCompletedTasks / this.totalTasks) * 100

                return `(${percentage.toFixed(2)}% of total employee)`
            },

          isProductivitySummary () {
            return this.selectedType === 'Productivity (Summary)';
          },

          isProductivityHourly () {
            return this.selectedType === 'Productivity (Hourly)';
          },

          isGoal () {
            return this.selectedType === 'Goal Metrics';
          },

          isAdmin () {
            return this.$store.state.isAdmin;
          },
        },

        watch: {
            selectedLeft () {
                if (!this.isGoal) {
                    this.resetChart();
                }
                localStorage.activityChart_selectedLeft = this.selectedLeft;
            },
            selectedRight () {
                if (!this.isGoal) {
                    this.resetChart();
                }
                localStorage.activityChart_selectedRight = JSON.stringify(this.selectedRight);
            },
            selectedType() {
                localStorage.activityChart_selectedType = this.selectedType;
                this.resetSecondarySelections();
            },
        },

        methods: {
            resetSecondarySelections () {
                this.selectedRight = DEFAULT_RIGHT_SELECTION;
                this.selectedLeft = DEFAULT_LEFT_SELECTION;
                this.resetChart();
            },
            async getChartData () {
              const paramKeys = {
                range: 'selectedLeft',
                visibility: 'selectedRight',
                type: 'selectedType',
              };
              const params = {};

              for (const [paramKey, value] of Object.entries(paramKeys)) {
                if (paramKey === 'type' && this.isProductivityHourly) {
                  params[paramKey] = 'hourly';
                } else if (paramKey === 'visibility') {
                  params[paramKey] = this[value].name;
                } else if (this[value]) {
                  params[paramKey] = this[value];
                }
              }

              const data = await axios.get(`company/${this.company.id}/chart`, { params }).
                  then(data => data.data.data);


                this.totalTasks = Object.values(data).reduce((total, value) => total + value, 0);

                this.totalCompletedTasks = this.totalTasks

              this.chartOptions.labels = Object.keys(data).map(key => {
                const placement = this.isProductivityHourly ? 'Hours' : 'Tasks';
                const value = this.isProductivityHourly ? moment.duration(data[key], 'seconds').asHours().toFixed(2) : data[key];
                let percentageCompleted = (
                    data[key] / this.totalCompletedTasks
                ) * 100;

                return `${key} (${percentageCompleted.toFixed(1)}% - ${value} ${placement})`;
              });

                Object.values(data).forEach(item => {
                    this.series.push(item)
                })


                this.ready = true
            },

            resetChart: _.debounce(function () {
                this.ready = false
                this.series = []
                this.chartOptions.labels = []
                this.getChartData()
            }, 250),

            generateVisibilityOptions () {
                this.userGroups.forEach(item => {
                    this.optionsRight.push({
                      name: item.id,
                      label: `Team ${item.name}`,
                    })
                })

                if (this.isAdmin) {
                    this.optionsRight.push({
                      name: 'company',
                      label: 'Company'
                    })
                }
            },

            registerEchoListener () {
                window.Echo.private(`tasks-updated.${this.company.id}`).listen(
                    '.tasks-updated',
                    e => {
                        this.resetChart();
                    },
                )
            },
        },

        mounted () {
            this.generateVisibilityOptions()
            this.getChartData()
            this.registerEchoListener()
        },
    }
</script>

<style scoped lang="scss">
    .chart {
        margin-top: 28px;

        .chart-title {
            color: #303030;
            font-family: "Open Sans";
            font-size: 16px;
            font-weight: bold;
            letter-spacing: 0;
        }

        .chart-desc {
            color: #8997aa;
            font-family: "Open Sans";
            font-size: 14px;
            font-weight: bold;
            letter-spacing: 0;
            line-height: 24px;
        }
    }

    .multiselect .arrow {
        z-index: 9 !important;
    }
</style>
